'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Navbar from '@/components/landing/navbar';
import Footer from '@/components/landing/footer';
import { CLIInstall } from '@/components/landing/cli-install';
import { AnimatedCounter } from '@/components/landing/animated-counter';
import { ScoreRing } from '@/components/coaching/ScoreRing';
import { capture } from '@/lib/analytics/posthog';
import { useTrackSectionView } from '@/hooks/useTrackSectionView';
import {
  DEMO_DAILY_SPEND,
  DEMO_ROI_TIMELINE,
  DEMO_CONVERSATION,
} from './mock-demo-data';
import { chartPalette } from '@/lib/chart-theme';

// ---------------------------------------------------------------------------
// Score bar sub-component (shared with SessionExplorerShowcase)
// ---------------------------------------------------------------------------

function ScoreBar({ label, score, tip }: { label: string; score: number; tip?: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-graphite">{label}</span>
        <span className="font-mono text-xs font-semibold tabular-nums text-ink">{score}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-vellum" role="presentation">
        <div
          className="h-full rounded-full bg-primary transition-all duration-700"
          style={{ width: `${score}%` }}
        />
      </div>
      {tip && <p className="text-[11px] text-graphite/70 leading-relaxed">{tip}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step section wrapper — alternating layout
// ---------------------------------------------------------------------------

function StepSection({
  stepNumber,
  children,
  sectionName,
}: {
  stepNumber: number;
  children: React.ReactNode;
  sectionName: string;
}) {
  const ref = useRef<HTMLElement>(null);
  useTrackSectionView(sectionName, ref);

  return (
    <section ref={ref} className="border-t border-rule px-4 py-20">
      <div className="mx-auto max-w-5xl">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {stepNumber}
          </span>
          <span className="text-xs font-semibold uppercase tracking-widest text-primary">
            Step {stepNumber}
          </span>
        </div>
        {children}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Spend chart data — formatted for recharts
// ---------------------------------------------------------------------------

const chartData = DEMO_DAILY_SPEND.slice(0, 14).map((d) => ({
  date: d.date.slice(5), // "MM-DD"
  openai: d.openai,
  anthropic: d.anthropic,
  google: d.google,
  total: d.openai + d.anthropic + d.google,
}));

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function HowItWorksPage() {
  const heroRef = useRef<HTMLElement>(null);
  const [typedCommand, setTypedCommand] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useTrackSectionView('how_it_works_hero', heroRef);

  // Terminal typing animation
  useEffect(() => {
    const command = 'npx @getverbal/cli init';
    setTypedCommand('');
    setIsTyping(true);
    let i = 0;
    const timer = setInterval(() => {
      if (i < command.length) {
        setTypedCommand(command.slice(0, i + 1));
        i++;
      } else {
        setIsTyping(false);
        clearInterval(timer);
      }
    }, 60);
    return () => clearInterval(timer);
  }, []);

  const coachingDimensions = DEMO_CONVERSATION.coachingInsights.dimensions;

  return (
    <div className="min-h-dvh bg-paper">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="px-4 pb-16 pt-20">
        <div className="mx-auto max-w-3xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5">
            <span className="font-sans text-sm font-medium text-primary">2 min setup · No credit card</span>
          </div>
          <h1 className="mb-6 font-serif-display text-5xl md:text-6xl font-semibold text-ink text-balance">
            How Verbal Works
          </h1>
          <p className="mb-10 max-w-xl font-sans text-lg text-graphite text-pretty">
            One command gives you complete visibility into every AI dollar you spend — across
            tools, models, and providers.
          </p>
          <CLIInstall
            variant="large"
            className="max-w-md"
            location="how_it_works_hero"
          />
        </div>
      </section>

      {/* Step 1: Install */}
      <StepSection stepNumber={1} sectionName="how_it_works_step_install">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          {/* Left: copy */}
          <div>
            <h2 className="mb-4 font-serif-display text-3xl font-semibold text-ink">
              Install in 2 Minutes
            </h2>
            <p className="mb-6 font-sans text-base text-graphite text-pretty">
              One command installs the Verbal CLI. No configuration, no agent to run — it
              auto-detects the AI tools you already use (Claude, Cursor, Copilot, ChatGPT, and
              more) and starts tracking immediately.
            </p>
            <ul className="space-y-3">
              {[
                'Works with your existing tools — no API key changes',
                'Detects Claude Code, Cursor, GitHub Copilot automatically',
                'Cloud-synced with automatic PII redaction — opt out any time',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 font-sans text-sm text-graphite">
                  <span aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-chart-2/20 text-center text-[10px] font-semibold leading-4 text-chart-2">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: terminal */}
          <div className="overflow-hidden rounded-sm border border-rule bg-paper">
            <div className="flex items-center gap-2 border-b border-rule bg-vellum px-4 py-3">
              <div className="h-3 w-3 rounded-full bg-destructive/70" aria-hidden="true" />
              <div className="h-3 w-3 rounded-full bg-chart-4/70" aria-hidden="true" />
              <div className="h-3 w-3 rounded-full bg-chart-2/70" aria-hidden="true" />
              <span className="ml-4 font-mono text-xs text-graphite">Terminal</span>
            </div>
            <div className="p-6 font-mono text-sm">
              <div className="flex items-center gap-2">
                <span className="text-primary" aria-hidden="true">$</span>
                <span className="text-ink" aria-live="polite">{typedCommand}</span>
                {isTyping && (
                  <span className="animate-pulse text-primary" aria-hidden="true">|</span>
                )}
              </div>
              <div className="mt-4 space-y-1 text-graphite">
                {!isTyping && (
                  <>
                    <p className="text-chart-2">Detecting AI tools&hellip;</p>
                    <p className="text-graphite/70">✓ Claude Code detected</p>
                    <p className="text-graphite/70">✓ Cursor detected</p>
                    <p className="text-graphite/70">✓ GitHub Copilot detected</p>
                    <p className="mt-2 text-chart-2">
                      Dashboard ready at{' '}
                      <span className="text-primary">http://localhost:4000</span>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </StepSection>

      {/* Step 2: Connect */}
      <StepSection stepNumber={2} sectionName="how_it_works_step_connect">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          {/* Left: mini dashboard mockup */}
          <div className="order-2 md:order-1">
            <div className="overflow-hidden rounded-sm border border-rule bg-paper">
              <div className="border-b border-rule px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-graphite">
                  Live data arriving...
                </p>
              </div>
              <div className="p-4">
                <div className="mb-4 grid grid-cols-3 gap-3">
                  {[
                    { label: 'Events today', value: '1', suffix: '' },
                    { label: 'Tokens used', value: '2', suffix: '.2K' },
                    { label: 'Cost so far', value: '0', suffix: '.04' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-sm border border-rule bg-vellum p-3 text-center"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-graphite">
                        {stat.label}
                      </p>
                      <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-ink">
                        {stat.value}
                        <span className="text-graphite">{stat.suffix}</span>
                      </p>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-graphite">
                    First event
                  </p>
                  <div className="flex items-center justify-between rounded-sm border border-rule bg-vellum px-3 py-2">
                    <div>
                      <p className="font-sans text-xs font-semibold text-ink">claude-sonnet-4-6</p>
                      <p className="font-sans text-[11px] text-graphite">Cursor · 3 min ago</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xs font-semibold tabular-nums text-ink">
                        2,200 tokens
                      </p>
                      <p className="font-mono text-xs tabular-nums text-primary">$0.04</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: copy */}
          <div className="order-1 md:order-2">
            <h2 className="mb-4 font-serif-display text-3xl font-semibold text-ink">
              Automatic Detection
            </h2>
            <p className="mb-6 font-sans text-base text-graphite text-pretty">
              Verbal hooks into your existing AI tools the moment you run the first command. No
              manual logging, no code changes — your first data point appears within seconds.
            </p>
            <ul className="space-y-3">
              {[
                'Hooks into MCP protocol for Claude Code sessions',
                'Captures every API call via local proxy (optional)',
                'Tracks Cursor, Copilot usage from editor metadata',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 font-sans text-sm text-graphite">
                  <span aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-chart-2/20 text-center text-[10px] font-semibold leading-4 text-chart-2">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </StepSection>

      {/* Step 3: Track */}
      <StepSection stepNumber={3} sectionName="how_it_works_step_track">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          {/* Left: copy */}
          <div>
            <h2 className="mb-4 font-serif-display text-3xl font-semibold text-ink">
              Unified Spend View
            </h2>
            <p className="mb-6 font-sans text-base text-graphite text-pretty">
              See every dollar across OpenAI, Anthropic, and Google in one chart. Spot weekend
              dips, model switches, and cost trends as they happen.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Providers', value: 12, suffix: '+' },
                { label: 'Avg savings', value: 47, suffix: '%' },
                { label: 'Teams using it', value: 500, suffix: '+' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-mono text-2xl font-semibold tabular-nums text-ink">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </p>
                  <p className="mt-0.5 font-sans text-xs text-graphite">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: chart */}
          <div className="overflow-hidden rounded-sm border border-rule bg-paper p-4">
            <p className="mb-3 font-sans text-xs font-semibold uppercase tracking-widest text-graphite">
              Daily AI Spend (14 days)
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="0" stroke={chartPalette.grid} />
                <XAxis
                  dataKey="date"
                  tick={{ fontFamily: 'var(--font-mono)', fontSize: 9, fill: chartPalette.axis }}
                  tickLine={false}
                  axisLine={false}
                  interval={2}
                />
                <YAxis
                  tick={{ fontFamily: 'var(--font-mono)', fontSize: 9, fill: chartPalette.axis }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => `$${v}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-paper)',
                    border: '1px solid var(--color-rule)',
                    borderRadius: '2px',
                    fontSize: '11px',
                  }}
                  formatter={(value, name) => [`$${Number(value)}`, String(name)]}
                />
                <Area
                  type="monotone"
                  dataKey="openai"
                  stackId="1"
                  stroke="var(--provider-openai)"
                  fill="var(--provider-openai)"
                  fillOpacity={0.3}
                  name="OpenAI"
                />
                <Area
                  type="monotone"
                  dataKey="anthropic"
                  stackId="1"
                  stroke="var(--provider-anthropic)"
                  fill="var(--provider-anthropic)"
                  fillOpacity={0.3}
                  name="Anthropic"
                />
                <Area
                  type="monotone"
                  dataKey="google"
                  stackId="1"
                  stroke="var(--provider-google)"
                  fill="var(--provider-google)"
                  fillOpacity={0.3}
                  name="Google"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </StepSection>

      {/* Step 4: Optimize */}
      <StepSection stepNumber={4} sectionName="how_it_works_step_optimize">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          {/* Left: analysis card */}
          <div className="order-2 md:order-1">
            <div className="overflow-hidden rounded-sm border border-rule bg-paper">
              <div className="border-b border-rule px-4 py-3">
                <p className="font-sans text-xs font-semibold uppercase tracking-widest text-graphite">
                  Prompt Analysis — claude-sonnet-4-6
                </p>
              </div>
              <div className="p-4">
                {/* Score ring + overall */}
                <div className="mb-4 flex items-center gap-4">
                  <ScoreRing
                    score={DEMO_CONVERSATION.coachingInsights.overall}
                    size={56}
                    strokeWidth={5}
                  />
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-graphite">
                      Overall Score
                    </p>
                    <p className="font-mono text-2xl font-semibold text-ink">
                      {DEMO_CONVERSATION.coachingInsights.overall}
                      <span className="ml-1 font-sans text-sm font-normal text-graphite">/ 100</span>
                    </p>
                  </div>
                </div>

                {/* Score bars */}
                <div className="mb-4 space-y-3">
                  {coachingDimensions.map((d) => (
                    <ScoreBar key={d.label} label={d.label} score={d.score} />
                  ))}
                </div>

                {/* Rewrite suggestion */}
                <div className="rounded-sm border border-primary/30 bg-primary/5 p-3">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-primary">
                    Suggested Rewrite
                  </p>
                  <p className="line-clamp-3 font-sans text-xs text-ink leading-relaxed">
                    {DEMO_CONVERSATION.coachingInsights.rewrite}
                  </p>
                  <p className="mt-2 font-sans text-[10px] text-primary font-medium">
                    Est. token reduction: ~340 tokens
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: copy */}
          <div className="order-1 md:order-2">
            <h2 className="mb-4 font-serif-display text-3xl font-semibold text-ink">
              Prompt Coaching
            </h2>
            <p className="mb-6 font-sans text-base text-graphite text-pretty">
              Every session gets a prompt quality score across 5 dimensions. Verbal spots
              ambiguous instructions, missing context, and wasted tokens — then shows you exactly
              how to fix them.
            </p>
            <ul className="space-y-3">
              {[
                'Scored across clarity, specificity, structure, efficiency, and context',
                'Rewrite suggestions that cut token usage by 20–40%',
                'Trend tracking: watch your scores improve week over week',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 font-sans text-sm text-graphite">
                  <span aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-chart-2/20 text-center text-[10px] font-semibold leading-4 text-chart-2">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </StepSection>

      {/* Step 5: Save */}
      <StepSection stepNumber={5} sectionName="how_it_works_step_save">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          {/* Left: copy */}
          <div>
            <h2 className="mb-4 font-serif-display text-3xl font-semibold text-ink">
              Track Your ROI
            </h2>
            <p className="mb-6 font-sans text-base text-graphite text-pretty">
              Verbal tracks the ROI of your AI spend week over week. As you right-size models and
              improve your prompts, watch your cost-per-token drop while output quality holds.
            </p>
            <ul className="space-y-3">
              {[
                'Model right-sizing: switch to cheaper models for simple tasks',
                'Prompt efficiency: same output for fewer tokens',
                'Weekly spend trend against team benchmarks',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 font-sans text-sm text-graphite">
                  <span aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-chart-2/20 text-center text-[10px] font-semibold leading-4 text-chart-2">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: before/after comparison */}
          <div className="overflow-hidden rounded-sm border border-rule bg-paper">
            <div className="border-b border-rule px-4 py-3">
              <p className="font-sans text-xs font-semibold uppercase tracking-widest text-graphite">
                4-Week Optimization Progress
              </p>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {DEMO_ROI_TIMELINE.map((week, i) => (
                  <div
                    key={week.week}
                    className="flex items-center justify-between rounded-sm border border-rule bg-vellum px-3 py-2.5"
                  >
                    <div>
                      <p className="font-sans text-xs font-semibold text-ink">{week.week}</p>
                      <p className="font-sans text-[11px] text-graphite">
                        {week.hoursSaved} hours saved
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-semibold tabular-nums text-ink">
                        ${week.cost.toLocaleString()}
                      </p>
                      <p
                        className={`font-sans text-[11px] font-medium tabular-nums ${
                          i > 0 ? 'text-chart-2' : 'text-graphite'
                        }`}
                      >
                        {i === 0
                          ? 'baseline'
                          : `-$${(DEMO_ROI_TIMELINE[0].cost - week.cost).toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-sm border border-chart-2/30 bg-chart-2/5 px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <p className="font-sans text-xs font-semibold text-chart-2">Total saved in 4 weeks</p>
                  <p className="font-mono text-sm font-semibold tabular-nums text-chart-2">
                    -${(DEMO_ROI_TIMELINE[0].cost - DEMO_ROI_TIMELINE[3].cost).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StepSection>

      {/* Final CTA */}
      <FinalCTA />

      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Final CTA section
// ---------------------------------------------------------------------------

function FinalCTA() {
  const ref = useRef<HTMLElement>(null);
  useTrackSectionView('how_it_works_final', ref);

  return (
    <section ref={ref} className="border-t border-rule bg-vellum px-4 py-20">
      <div className="mx-auto max-w-2xl">
        <h2 className="mb-4 font-serif-display text-4xl font-semibold text-ink text-balance">
          Start Tracking in 2 Minutes
        </h2>
        <p className="mb-8 font-sans text-lg text-graphite">
          No signup. No credit card. Just one command and you have full visibility.
        </p>
        <CLIInstall
          variant="large"
          className="mb-6 max-w-md"
          location="how_it_works_final"
        />
        <p className="font-sans text-sm text-graphite">
          Or{' '}
          <Link
            href="/login"
            onClick={() => capture('cta_clicked', { location: 'how_it_works_final', cta: 'signup' })}
            className="text-salmon-text underline-offset-4 hover:underline"
          >
            create a free account
          </Link>{' '}
          to sync across devices.
        </p>
      </div>
    </section>
  );
}
